import React, { useCallback, useEffect } from 'react';
import classes from './ExaminationsTab.module.css';

import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { fetchExaminationByID, selectExaminations } from '../../../../store/slices/examinations';
import { Accordion } from '../../Accordion/Accordion';
import downloadIcon from '../../../../assets/icons/download-icon.svg';
import { fetchVitalSigns, selectVitalSigns } from '../../../../store/slices/vitalSigns';
import { client } from '../../../../networking/client';
import { useTranslation } from '../../../../hooks/useTranslations';

type Props = {
  startExamination: (examTypeId: number, callback: (value: string) => void) => void;
  report?: boolean;
  workOrderId: number | undefined;
};

const TemperatureExamination: React.FC<Props> = ({ workOrderId }) => {
  const { translations } = useTranslation();
  const [examinationId, setExaminationId] = React.useState<string>();
  const dispatch = useAppDispatch();
  const examinations = useAppSelector(selectExaminations);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isFetched, setIsFetched] = React.useState<boolean>(false);
  const vitalSigns = useAppSelector(selectVitalSigns);

  const examination = React.useMemo(() => {
    const _exam = examinations.find(
      (examination) => examination.examination_name === 'Temperature'
    );
    if (_exam) {
      setExaminationId(_exam.work_order_examination_id);
    }
    return _exam;
  }, [examinations]);

  const vitalSignTemperature = React.useMemo(() => {
    return vitalSigns.find((vs) => vs.vital_sign_name === 'Temperature');
  }, [vitalSigns]);

  const hasStartedExamination = React.useMemo(() => {
    return !!examinations.find((examination) => examination.work_order_examination_id);
  }, [examinations]);

  const fetchData = async () => {
    setLoading(true);
    if (examinationId) {
      await dispatch(fetchExaminationByID(examinationId));
      setIsFetched(true);
      return;
    }
    setLoading(false);
  };

  const setTemperatureVitalSign = useCallback(async () => {
    try {
      if (vitalSignTemperature && examination?.valueQuantity?.value) {
        await client.put(
          `/healthpoint/vital_sign_result/${workOrderId}/${vitalSignTemperature.vital_sign_id}`,
          {
            vitalSignValueResult: examination.valueQuantity.value,
          }
        );
      }
    } catch (error) {
      console.error('Error setting temperature:', error);
    }
  }, [translations, vitalSignTemperature, examination, workOrderId]);

  useEffect(() => {
    dispatch(fetchVitalSigns());
  }, [dispatch]);

  useEffect(() => {
    if (isFetched) setTemperatureVitalSign();
  }, [vitalSignTemperature, examination, workOrderId, setTemperatureVitalSign, isFetched]);

  return (
    <Accordion
      disableExpand={true}
      header={
        <div className={classes.reportTabHeader}>
          <div className={classes.headerName}>
            <div className={classes.reportTabHeaderMain}>
              {translations('examinations.temperature')}
            </div>
            {!isFetched && (
              <div
                className={classes.downloadButton}
                style={{
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  if (!loading) {
                    fetchData();
                  }
                }}
              >
                {loading ? (
                  <span>{translations('examinations.loading')}</span>
                ) : (
                  <span>{translations('examinations.fetch')}</span>
                )}
                {!loading && (
                  <img className={classes.downloadIcon} src={downloadIcon} alt="download" />
                )}
              </div>
            )}
            {isFetched && examination?.valueQuantity?.value && (
              <div className={classes.temperature}>
                <div>{`${examination?.valueQuantity?.value} ${examination.valueQuantity.unit}`}</div>
              </div>
            )}
          </div>
          {hasStartedExamination && (
            <div className={classes.reportTabHeaderSecond}>
              {format(new Date(), 'dd.MM.yyyy')}•{format(new Date(), 'HH:mm')}
            </div>
          )}
        </div>
      }
      containerStyle={{
        boxShadow: 'none',
        border: '1px solid #D7D7D7',
        margin: 10,
      }}
      headerStyle={{
        padding: '16px 10px',
      }}
    >
      <div className={classes.rightContainer}>
        <div className={classes.examinationContainer}>{examination?.valueQuantity?.value}</div>
      </div>
    </Accordion>
  );
};

export { TemperatureExamination };
